import { joiResolver } from '@hookform/resolvers/joi';
import { DefaultValues, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { MutationStatus } from 'react-query';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { ExamSchoolFormData, SchoolFormData, SiblingFormData } from '../../../models';
import examSchoolingSchema from '../schema/examSchooling';
import {
  CaeCertificateDetailsModelProps,
  PostNonNationalExamSchoolingDetailRequest,
  usePostNonNationalExamSchoolingDetail,
} from '../../../api-refactored/non-national-exam-registration/usePostNonNationalExamSchoolingDetail';
import RouteKey from '../../../routes/key';
import { IOptionType } from '../../../api/candidate/exam/model';
import { GetNonNationalExamSchoolingDetailsResponse } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSchoolingDetails';
import { EXAM_TYPE } from '../../../routes/ExamRoute';
import { ExamSchoolingDetailsPageLocation } from '../../../pages/examRegistration/NonNationalExam/AEIS/ExamSchoolingDetails';
import { GenericErrorResponse } from '../../../api-refactored/typings';
import { ValidCeqTestDatesModel } from '../components/nonNationalExam/ExamSchoolingDetailsForm';

const { uploadSupportingDocuments } = RouteKey.exam.child;
const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const defaultHasData = (
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined,
  key: keyof GetNonNationalExamSchoolingDetailsResponse
) => {
  if (
    examSchoolingDetailsData &&
    Object.prototype.hasOwnProperty.call(examSchoolingDetailsData, key)
  ) {
    return examSchoolingDetailsData[key] ? 'yes' : 'no';
  }
  return undefined;
};

const schoolsDefault = (
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined,
  birthCountryData: IOptionType[] | undefined
): SchoolFormData[] =>
  examSchoolingDetailsData && examSchoolingDetailsData.examSchoolInfoModels
    ? examSchoolingDetailsData?.examSchoolInfoModels.map((v) => {
        const countryDefaultItem: IOptionType | undefined = birthCountryData?.find(
          (b) => b.code === (typeof v.country !== 'string' ? v.country.code : v.country)
        );
        return {
          country: JSON.parse(JSON.stringify(countryDefaultItem || {})),
          dateOfAdmission: v.dateOfAdmission,
          dateOfCompletion: v.dateOfCompletion,
          highestLevelPassed: v.highestLevelPassed,
          schoolName: v.schoolName,
          typeOfSchool: v.typeOfSchool,
        };
      })
    : [];

const siblingsDefault = (
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined,
  siblingExamLevelData: IOptionType[] | undefined
): SiblingFormData[] =>
  examSchoolingDetailsData && examSchoolingDetailsData.examSiblingInfoModels
    ? examSchoolingDetailsData?.examSiblingInfoModels.map((v) => {
        const defaultLevel = siblingExamLevelData?.find((o) => o.description === v.level);

        return {
          dateOfBirth: v.dateOfBirth,
          gender: v.gender,
          level: JSON.parse(JSON.stringify(defaultLevel)),
          name: v.name,
        };
      })
    : [];

const createCaeModel = (submitData: ExamSchoolFormData): CaeCertificateDetailsModelProps => ({
  centreRefNo: submitData.centerReferenceNo,
  ceqTypeUuid: submitData.ceqType.code,
  country: {
    code: submitData.countryCEQ.code,
    description: submitData.countryCEQ.description,
  },
  dateTaken: dayjs(submitData.dateOfCEQ).format(DATE_FORMAT),
  listeningScore: submitData.listening,
  overallScore: submitData.overall,
  readingScore: submitData.reading,
  speakingScore: submitData.speaking,
  verificationNo: submitData.verificationNo,
  writingScore: submitData.writing,
});

const createExamSchoolingDetailPayload = (
  caeModel: CaeCertificateDetailsModelProps | null,
  submitData: ExamSchoolFormData,
  schools: SchoolFormData[],
  siblings: SiblingFormData[],
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined
): PostNonNationalExamSchoolingDetailRequest => ({
  caeCertificateDetailsModel: caeModel,
  schoolInfo: submitData.hasStudyingInSingapore === 'yes',
  siblingInfo: submitData.hasSibling === 'yes',
  examSchoolInfoModels:
    submitData.hasStudyingInSingapore === 'yes'
      ? schools.map((value) => ({
          country:
            typeof value.country !== 'string'
              ? {
                  code: value.country.code,
                  description: value.country.description,
                }
              : { description: '', code: '' },
          dateOfAdmission: dayjs(value.dateOfAdmission).format(DATE_FORMAT),
          dateOfCompletion: dayjs(value.dateOfCompletion).format(DATE_FORMAT),
          highestLevelPassed: value.highestLevelPassed,
          schoolName: value.schoolName,
          typeOfSchool: value.typeOfSchool,
        }))
      : [],
  examSiblingInfoModels:
    submitData.hasSibling === 'yes'
      ? siblings.map((value) => ({
          dateOfBirth: dayjs(value.dateOfBirth).format(DATE_FORMAT),
          gender: value.gender,
          level: typeof value.level !== 'string' ? value.level.code : '',
          name: value.name,
        }))
      : [],
  selectedAdmissionLevel: submitData.selectedAdmissionLevel,
  motherTongueLanguageToBeTaken:
    submitData.motherTongueLanguageToBeTaken.code ||
    examSchoolingDetailsData?.motherTongueLanguageToBeTaken,
});

const createDefaultValues = (
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined,
  ceqTypeDefault: IOptionType | undefined,
  countryDefault: IOptionType | undefined,
  selectedAdmissionLevel: string
): DefaultValues<ExamSchoolFormData> => ({
  centerReferenceNo: examSchoolingDetailsData?.caeCertificateDetailsModel?.centreRefNo,
  dateOfCEQ: examSchoolingDetailsData?.caeCertificateDetailsModel?.dateTaken || '',
  hasSibling: defaultHasData(examSchoolingDetailsData, 'siblingInfo'),
  hasStudyingInSingapore: defaultHasData(examSchoolingDetailsData, 'schoolInfo'),
  listening: examSchoolingDetailsData?.caeCertificateDetailsModel?.listeningScore,
  overall: examSchoolingDetailsData?.caeCertificateDetailsModel?.overallScore,
  reading: examSchoolingDetailsData?.caeCertificateDetailsModel?.readingScore,
  speaking: examSchoolingDetailsData?.caeCertificateDetailsModel?.speakingScore,
  verificationNo: examSchoolingDetailsData?.caeCertificateDetailsModel?.verificationNo,
  writing: examSchoolingDetailsData?.caeCertificateDetailsModel?.writingScore,
  ceqType: ceqTypeDefault,
  countryCEQ: countryDefault,
  selectedAdmissionLevel,
});

interface CheckScoreReturn {
  maxScore: number;
  minScore: number;
  minOverallScore: number;
}

const checkScore = (selectedAdmissionLevel: string | undefined): CheckScoreReturn => {
  const maxScore: number = 999;
  const minScore: number = 0;
  let minOverallScore: number = 100;

  switch (selectedAdmissionLevel) {
    case 'PRIMARY 3':
      minOverallScore = 120;
      break;
    case 'PRIMARY 4':
      minOverallScore = 130;
      break;
    case 'PRIMARY 5':
      minOverallScore = 140;
      break;
    default:
      break;
  }

  return { maxScore, minScore, minOverallScore };
};

interface ExamSchoolingDetailsProps {
  uuid: string;
  isFullForm: boolean;
  ceqTypeDefault: IOptionType | undefined;
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined;
  countryDefault: IOptionType | undefined;
  selectedAdmissionLevel: string;
  selectedAdmissionLevelDescription: string;
  birthCountryData: IOptionType[] | undefined;
  siblingExamLevelData: IOptionType[] | undefined;
  isOpsAdmin?: boolean;
  hasViewAccess: boolean;
  hasEditAccess: boolean;
  validCeqTestDates: ValidCeqTestDatesModel | undefined;
}

interface ExamSchoolingDetailsReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitForm: (submitData: ExamSchoolFormData) => void;
  /**
   * Form to send edit & delete
   */
  formHook: UseFormReturn<ExamSchoolFormData>;
  /**
   * React query mutation object
   */
  postExamSchoolingDetailStatus: MutationStatus;
  hasStudyingInSingapore: string;
  setHasStudyingInSingapore: Dispatch<SetStateAction<string>>;
  hasSibling: string;
  setHasSibling: Dispatch<SetStateAction<string>>;
  schools: SchoolFormData[];
  setSchools: Dispatch<SetStateAction<SchoolFormData[]>>;
  siblings: SiblingFormData[];
  setSiblings: Dispatch<SetStateAction<SiblingFormData[]>>;
  isDisableAddSchool: boolean;
  isDisableAddSibling: boolean;
  schoolEmptyMessage: string;
  setSchoolEmptyMessage: Dispatch<SetStateAction<string>>;
  siblingEmptyMessage: string;
  setSiblingEmptyMessage: Dispatch<SetStateAction<string>>;
  admissionLevel: string;
  isAeis: boolean;
  submitButtonClick: () => void;
  createErrorMessage: string;
  postError: AxiosError<GenericErrorResponse> | null;
  isErrorPost: boolean;
}

/**
 * A hook to handle search functionality
 * @returns
 */
export const useExamSchoolingDetails = ({
  uuid,
  isFullForm,
  ceqTypeDefault,
  countryDefault,
  examSchoolingDetailsData,
  selectedAdmissionLevel,
  birthCountryData,
  siblingExamLevelData,
  isOpsAdmin = false,
  selectedAdmissionLevelDescription,
  hasViewAccess,
  hasEditAccess,
  validCeqTestDates,
}: ExamSchoolingDetailsProps): ExamSchoolingDetailsReturn => {
  const history = useHistory();
  const { state } = useLocation<ExamSchoolingDetailsPageLocation>();
  const isAeis = state?.examType === EXAM_TYPE.AEIS || state?.examType === EXAM_TYPE.SAEIS;
  const { maxScore, minScore, minOverallScore } = checkScore(selectedAdmissionLevelDescription);

  const formHook = useForm<ExamSchoolFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
        examSchoolingSchema(
            validCeqTestDates,
            isFullForm,
            maxScore,
            minScore,
            selectedAdmissionLevelDescription,
            minOverallScore,
            isOpsAdmin
        )
    ),
    defaultValues: createDefaultValues(
      examSchoolingDetailsData,
      ceqTypeDefault,
      countryDefault,
      selectedAdmissionLevel
    ),
  });

  const admissionLevel = useWatch({
    control: formHook.control,
    name: 'selectedAdmissionLevel',
    defaultValue: selectedAdmissionLevel,
  });

  const [hasStudyingInSingapore, setHasStudyingInSingapore] = useState<string>('');
  const [hasSibling, setHasSibling] = useState<string>('');
  const [schools, setSchools] = useState<SchoolFormData[]>([
    ...schoolsDefault(examSchoolingDetailsData, birthCountryData),
  ]);
  const [siblings, setSiblings] = useState<SiblingFormData[]>([
    ...siblingsDefault(examSchoolingDetailsData, siblingExamLevelData),
  ]);
  const [isDisableAddSchool, setIsDisableAddSchool] = useState<boolean>(false);
  const [isDisableAddSibling, setIsDisableAddSibling] = useState<boolean>(false);
  const [schoolEmptyMessage, setSchoolEmptyMessage] = useState<string>('');
  const [siblingEmptyMessage, setSiblingEmptyMessage] = useState<string>('');
  const [createErrorMessage, setCreateErrorMessage] = useState<string>('');

  useEffect(() => {
    if (examSchoolingDetailsData) {
      if (Object.prototype.hasOwnProperty.call(examSchoolingDetailsData, 'siblingInfo')) {
        setHasSibling(examSchoolingDetailsData?.siblingInfo ? 'yes' : 'no');
      }

      if (Object.prototype.hasOwnProperty.call(examSchoolingDetailsData, 'schoolInfo')) {
        setHasStudyingInSingapore(examSchoolingDetailsData?.schoolInfo ? 'yes' : 'no');
      }
    }
  }, [examSchoolingDetailsData]);

  useEffect(() => {
    if (schools.length > 0) {
      setSchoolEmptyMessage('');
    }
    setIsDisableAddSchool(schools.length > 0);
  }, [schools]);

  useEffect(() => {
    if (siblings.length > 0) {
      setSiblingEmptyMessage('');
    }
    setIsDisableAddSibling(siblings.length >= 3);
  }, [siblings]);

  const {
    mutate,
    status: postExamSchoolingDetailStatus,
    error,
    isError,
  } = usePostNonNationalExamSchoolingDetail({
    uuid,
    onSuccess: () => nextPage(),
  });

  const nextPage = () => {
    const pathname = `${uploadSupportingDocuments.route}`;
    history.push({
      pathname,
      state,
    });
  };

  const submitForm = (submitData: ExamSchoolFormData) => {
    if (examSchoolingDetailsData?.lockedByUser) {
      nextPage();
      return;
    }

    const caeModel: CaeCertificateDetailsModelProps | null = isFullForm
      ? createCaeModel(submitData)
      : null;

    const examSchoolingDetailsPayload = createExamSchoolingDetailPayload(
      caeModel,
      submitData,
      schools,
      siblings,
      examSchoolingDetailsData
    );

    mutate(examSchoolingDetailsPayload);
  };

  const submitButtonClick = () => {
    if (!hasEditAccess && hasViewAccess) {
      nextPage();
      return;
    }

    const isValidateSchools = hasStudyingInSingapore === 'yes' && schools.length === 0;
    const isValidateSiblings = hasSibling === 'yes' && siblings.length === 0;

    if (isValidateSchools) {
      setSchoolEmptyMessage('School Info is not allowed to be empty');
    }
    if (isValidateSiblings) {
      setSiblingEmptyMessage('Sibling Info is not allowed to be empty');
    }

    if (createErrorMessage) {
      setCreateErrorMessage('');
    }

    formHook.handleSubmit((formData) => {
      if (!isValidateSchools && !isValidateSiblings) {
        submitForm(formData);
      }
    })();
  };

  return {
    formHook,
    submitForm,
    postExamSchoolingDetailStatus,
    hasSibling,
    hasStudyingInSingapore,
    isDisableAddSchool,
    isDisableAddSibling,
    schoolEmptyMessage,
    schools,
    setHasSibling,
    setHasStudyingInSingapore,
    setSchoolEmptyMessage,
    setSchools,
    setSiblingEmptyMessage,
    setSiblings,
    siblingEmptyMessage,
    siblings,
    admissionLevel,
    isAeis,
    submitButtonClick,
    createErrorMessage,
    isErrorPost: isError,
    postError: error,
  };
};
